import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Loader2, Plus, Wifi, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useAutoSave } from "../hooks/useAutoSave";
import SaveStatus from "./SaveStatus";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";

// Shared sorting-light configuration, applied across Sorter 1, Sorter 2 AND
// Pick & Sort. Lifted out of the Pick & Sort panel because all of it is global
// or per-warehouse (never per-station):
//   • Label colours  — global palette (shelf letters, position numbers, and the
//                       two light-cue wheel colours).
//   • Light agents    — each on-site agent is mapped to a warehouse; it then
//                       drives every light-enabled bay in that warehouse,
//                       regardless of which station the bay belongs to.
//   • Light settings  — per-warehouse brightness % + auto-off delay.

const SHELVES = ["A", "B", "C", "D", "E"] as const;
// Six number colours, whatever a bay's width — wider bays cycle back onto these
// (positions 1/7/13 share a swatch, 2/8/14, and so on). Newport Sorter 2 bay 7
// is 18 wide but is three physically separate bays of 6, so the operator only
// ever sees one set of six at a time.
const POSITIONS = [1, 2, 3, 4, 5, 6] as const;

// Mirrors the long-standing Sorter 1 / Sorter 2 scheme in zonestorage.css
// (.letter-A … .letter-E), so the printed labels match both the scanner screen
// and the labels already on the racking. Text colour is not fixed — the label
// picks whichever of black/white contrasts more (readableTextOn).
const DEFAULT_SHELF_COLOURS: Record<string, string> = {
    A: "#3C89F0", B: "#3CB44B", C: "#FFE119", D: "#F58231", E: "#911EB4",
};
// Mirrors .number-1 … .number-6 in zonestorage.css. That CSS already repeats
// every 6 (its selectors list n, n+6, n+12), which is the same cycle
// positionColourKey applies here.
const DEFAULT_POSITION_COLOURS: Record<string, string> = {
    "1": "#808080", "2": "#000000", "3": "#A67C52",
    "4": "#BFEF45", "5": "#42D4F4", "6": "#F032E6",
};

// The light's colour wheel — the only colours the ADJ Focus Hybrid can show.
// (Its wheel has no true Pink/Cyan; the BE maps those to Purple/Light Blue.)
const WHEEL_COLOUR_NAMES = ["White", "Red", "Orange", "Yellow", "Green", "Blue", "Pink", "Cyan", "Magenta"] as const;

// timeout = the colour the beam snaps to (strobing) when the operator blows
// the location time target.
type LightColours = { active: string; complete: string; timeout: string };
type Palette = { shelf: Record<string, string>; position: Record<string, string>; light: LightColours };
// Remote network management: what the agent reports about its own network,
// the (sanitised — never the password) queued command, and the last outcome.
type AgentNetInfo = {
    ssid?: string | null; signal?: number | null; ips?: Record<string, string>; error?: string; at?: string;
    // Which BE the device is talking to (self-reported), and whether that is
    // an admin override rather than its systemd default.
    backend?: string; backend_override?: boolean;
};

// The two backends an agent can be pointed at from here. Mirrors the BE's
// allowlist — the panel only ever sends the label, never a URL.
const AGENT_BACKENDS: Record<string, string> = {
    staging: "https://jli-portal-be-staging.onrender.com",
    production: "https://wms.jlitrading.co.uk",
};
const backendLabelOf = (url?: string) => {
    if (!url) return null;
    const hit = Object.entries(AGENT_BACKENDS).find(([, u]) => u === url.replace(/\/+$/, ""));
    return hit ? hit[0] : "custom";
};
type AgentWifiNetwork = { ssid: string; signal: number; security: string; in_use: boolean };
type AgentCommandResult = { id: string; type: string; ok: boolean; detail?: string; networks?: AgentWifiNetwork[]; finished_at?: string };
type LightAgent = {
    agent_id: string; warehouse: string | null; station: string | null; last_seen_at: string | null; online: boolean;
    // Pinned to a single bay — the Sorter 2 put wall is a separate structure
    // with its own device. null = every bay of the assigned station.
    bay_id?: string | number | null;
    // Agent-measured scan→device cue latency (median over its recent cues);
    // null until the agent has reported recently.
    cue_latency_ms?: number | null; cue_latency_samples?: number | null;
    net_info?: AgentNetInfo | null;
    pending_command?: { id: string; type: string; requested_at?: string } | null;
    command_result?: AgentCommandResult | null;
};

// Sorter stations an agent can be scoped to. Empty value = drive every station
// in the warehouse (the default — for sites with one device for the whole site).
const STATIONS = ["pick_and_sort", "put_wall", "sorter_1", "sorter_2"] as const;

// Bays an agent can be pinned to, returned alongside the agents themselves.
type AgentBay = { bay_id: string | number; warehouse: string; station: string; name: string };

const normaliseHex = (v: string) => { const s = v.trim(); return s && !s.startsWith("#") ? `#${s}` : s; };
const safeSwatch = (v: string) => (/^#[0-9a-fA-F]{6}$/.test(v) ? v : "#000000");

const emptyPalette = (): Palette => ({
    shelf: { ...DEFAULT_SHELF_COLOURS },
    position: { ...DEFAULT_POSITION_COLOURS },
    light: { active: "Green", complete: "Yellow", timeout: "Red" },
});

const SortingSharedLighting = ({ warehouses: allWarehouses }: { warehouses: string[] }) => {
    const { t } = useTranslation();
    // Without cross_warehouse only the person's own warehouse is offered.
    const { canCross, own } = useWarehouseScope();
    const warehouses = useMemo(() => (canCross ? allWarehouses : allWarehouses.filter(w => w === own)), [canCross, own, allWarehouses]);

    // Light settings are per-warehouse, so this panel needs its own selector.
    const [warehouse, setWarehouse] = useState<string>(warehouses[0] ?? "");
    useEffect(() => {
        if ((!warehouse || !warehouses.includes(warehouse)) && warehouses.length) setWarehouse(warehouses[0]);
    }, [warehouses, warehouse]);

    // ── Global label palette ────────────────────────────────────────────────
    const [palette, setPalette] = useState<Palette>(emptyPalette());
    const [savedPalette, setSavedPalette] = useState<Palette>(emptyPalette());
    const [savingColours, setSavingColours] = useState(false);
    const paletteDirty = JSON.stringify(palette) !== JSON.stringify(savedPalette);
    // Don't auto-save half-typed hex values.
    const paletteValid = [...Object.values(palette.shelf), ...Object.values(palette.position)]
        .every(v => /^#[0-9a-fA-F]{6}$/.test(v));

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort-admin/colours`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && data?.shelf && data?.position) {
                    const light: LightColours = {
                        active: data.light?.active ?? "Green",
                        complete: data.light?.complete ?? "Yellow",
                        timeout: data.light?.timeout ?? "Red",
                    };
                    const p: Palette = { shelf: data.shelf, position: data.position, light };
                    setPalette(p);
                    setSavedPalette(p);
                }
            } catch { /* keep defaults */ }
        })();
        return () => { alive = false; };
    }, []);

    const handleSaveColours = async (): Promise<boolean> => {
        setSavingColours(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/colours`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(palette),
            });
            if (!res.ok) throw new Error("colours");
            const data = await res.json();
            if (data?.shelf && data?.position) {
                const p: Palette = { shelf: data.shelf, position: data.position, light: data.light ?? palette.light };
                setPalette(p);
                setSavedPalette(p);
            }
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "pas-colours-err" });
            return false;
        } finally {
            setSavingColours(false);
        }
    };

    useAutoSave(palette, {
        dirty: paletteDirty, saving: savingColours, enabled: paletteValid,
        save: handleSaveColours,
    });

    // ── Light agents ────────────────────────────────────────────────────────
    const [agents, setAgents] = useState<LightAgent[]>([]);
    const [agentBays, setAgentBays] = useState<AgentBay[]>([]);
    const [newAgentId, setNewAgentId] = useState("");
    const refreshAgents = useCallback(async () => {
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents`, {});
            if (!res.ok) return;
            const data = await res.json();
            setAgents(Array.isArray(data.agents) ? data.agents : []);
            setAgentBays(Array.isArray(data.bays) ? data.bays : []);
        } catch { /* leave last-known */ }
    }, []);
    useEffect(() => {
        refreshAgents();
        const id = setInterval(refreshAgents, 10000);
        return () => clearInterval(id);
    }, [refreshAgents]);

    const assignAgent = async (agentId: string, wh: string, station: string, bayId: string = "") => {
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents/assign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    agent_id: agentId, warehouse: wh || null, station: station || null, bay_id: bayId || null,
                }),
            });
            if (!res.ok) throw new Error("assign");
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-agent-err" });
        }
    };

    // Pre-register an agent by id (e.g. "portal-pd") before it has polled in, so
    // its warehouse can be assigned ahead of time. Reuses the upsert assign
    // endpoint with no warehouse; the admin then picks one from the row.
    const addAgent = async () => {
        const id = newAgentId.trim();
        if (!id) return;
        if (agents.some(a => a.agent_id === id)) {
            toast.error(t("pas_agent_exists"), { toastId: "pas-agent-err" });
            return;
        }
        await assignAgent(id, "", "");
        setNewAgentId("");
    };

    const removeAgent = async (agentId: string) => {
        if (!window.confirm(t("pas_confirm_remove_agent", { id: agentId }))) return;
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents/remove`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_id: agentId }),
            });
            if (!res.ok) throw new Error("remove");
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-agent-err" });
        }
    };

    // ── Per-agent network / WiFi modal ─────────────────────────────────────
    // The device is behind the site's NAT, so everything here is queued on the
    // BE and collected by the agent's ~15s control poll — the modal just
    // renders the agent row's net_info / pending_command / command_result and
    // polls faster while it's open so outcomes appear promptly.
    const [wifiAgentId, setWifiAgentId] = useState<string | null>(null);
    const [wifiSsid, setWifiSsid] = useState("");
    const [wifiPsk, setWifiPsk] = useState("");
    const wifiAgent = agents.find(a => a.agent_id === wifiAgentId) ?? null;
    useEffect(() => {
        if (!wifiAgentId) return;
        const id = setInterval(refreshAgents, 3000);
        return () => clearInterval(id);
    }, [wifiAgentId, refreshAgents]);

    const queueWifi = async (body: Record<string, unknown>) => {
        if (!wifiAgentId) return;
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents/wifi`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_id: wifiAgentId, ...body }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => null);
                toast.error(d?.message ?? t("server_error"), { toastId: "pas-wifi-err" });
                return;
            }
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-wifi-err" });
        }
    };
    const openWifi = (agentId: string) => { setWifiAgentId(agentId); setWifiSsid(""); setWifiPsk(""); };

    // Push a Focus Hybrid unit personality (Speed 2, WiFly off, reset) to every
    // head a device drives — over DMX, via the same control channel. The BE
    // resolves the DMX value from its allowlist; the panel only sends a name.
    const pushHeadPersonality = async (agentId: string, personality: string) => {
        if (!personality) return;
        if (!window.confirm(t("pas_heads_confirm", { id: agentId, personality: t(`pas_heads_${personality}` as never) }))) return;
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents/head`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_id: agentId, personality }),
            });
            if (!res.ok) throw new Error("head");
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-agent-err" });
        }
    };

    // Point a device at the other BE. The scan→light cue store is per-BE-
    // instance, so each agent must talk to the instance its site's scans hit
    // (e.g. Poland on production, Pembroke on staging). Queued over the same
    // control channel as WiFi; the agent restarts onto the choice and
    // auto-reverts if the target BE rejects its key.
    const switchBackend = async (agentId: string, backend: string) => {
        if (!AGENT_BACKENDS[backend]) return;
        if (!window.confirm(t("pas_backend_confirm", { id: agentId, backend: t(`pas_backend_${backend}` as never) }))) return;
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-agents/backend`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ agent_id: agentId, backend }),
            });
            if (!res.ok) throw new Error("backend");
            await refreshAgents();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-agent-err" });
        }
    };
    const wifiConnect = (connectNow: boolean) => {
        const s = wifiSsid.trim();
        if (!s) return;
        if (wifiPsk && (wifiPsk.length < 8 || wifiPsk.length > 63)) {
            toast.error(t("pas_wifi_password_open_hint"), { toastId: "pas-wifi-err" });
            return;
        }
        queueWifi({ action: "connect", ssid: s, psk: wifiPsk, connect_now: connectNow });
    };

    // ── Per-warehouse light settings (brightness % + auto-off delay) ──────────
    // `lightSettingsWh` records which warehouse the loaded values belong to,
    // so auto-save can't post stale values after a warehouse tab switch.
    type LightSettings = { brightness: number; off_ms: number; move_base_ms: number; move_step_ms: number; move_threshold: number };
    const DEFAULT_LIGHT_SETTINGS: LightSettings = { brightness: 100, off_ms: 20000, move_base_ms: 200, move_step_ms: 100, move_threshold: 250 };
    const [lightSettings, setLightSettings] = useState<LightSettings>(DEFAULT_LIGHT_SETTINGS);
    const [savedLightSettings, setSavedLightSettings] = useState<LightSettings>(DEFAULT_LIGHT_SETTINGS);
    const [lightSettingsWh, setLightSettingsWh] = useState<string | null>(null);
    const [savingLightSettings, setSavingLightSettings] = useState(false);
    const lightSettingsDirty = JSON.stringify(lightSettings) !== JSON.stringify(savedLightSettings);

    useEffect(() => {
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort-admin/light-settings?warehouse=${encodeURIComponent(warehouse)}`, {});
                if (!res.ok || !alive) return;
                const d = await res.json();
                const s = {
                    brightness: d.brightness ?? 100, off_ms: d.off_ms ?? 20000,
                    move_base_ms: d.move_base_ms ?? 200, move_step_ms: d.move_step_ms ?? 100,
                    move_threshold: d.move_threshold ?? 250,
                };
                setLightSettings(s); setSavedLightSettings(s); setLightSettingsWh(warehouse);
            } catch { /* keep defaults */ }
        })();
        return () => { alive = false; };
    }, [warehouse]);

    const handleSaveLightSettings = async (): Promise<boolean> => {
        setSavingLightSettings(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/light-settings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, ...lightSettings }),
            });
            if (!res.ok) throw new Error("save");
            const d = await res.json();
            const s = {
                brightness: d.brightness, off_ms: d.off_ms,
                move_base_ms: d.move_base_ms ?? 200, move_step_ms: d.move_step_ms ?? 100,
                move_threshold: d.move_threshold ?? 250,
            };
            setLightSettings(s); setSavedLightSettings(s);
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "pas-lightsettings-err" });
            return false;
        } finally {
            setSavingLightSettings(false);
        }
    };

    useAutoSave(lightSettings, {
        dirty: lightSettingsDirty, saving: savingLightSettings,
        enabled: !!warehouse && lightSettingsWh === warehouse,
        save: handleSaveLightSettings,
    });

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: "var(--color-text-muted, #6b7280)", marginBottom: 10 }}>
                {t("sorting_lighting_hint")}
            </div>

            {/* Global label palette — applies to every warehouse, bay and station.
                Collapsed by default: reference material once a site is set up. */}
            <details className="lighting-section">
                <summary className="lighting-head">
                    <ChevronRight size={16} className="lighting-caret" />
                    <strong>{t("pas_label_colours")}</strong>
                    {/* A strip of the palette so the collapsed card still says what it holds. */}
                    <span style={{ display: "inline-flex", gap: 3 }}>
                        {SHELVES.map(s => (
                            <span key={s} style={{ width: 10, height: 10, borderRadius: 3, background: safeSwatch(palette.shelf[s] ?? DEFAULT_SHELF_COLOURS[s]), display: "inline-block" }} />
                        ))}
                    </span>
                    <span className="lighting-head-status">
                        <SaveStatus dirty={paletteDirty} saving={savingColours} />
                    </span>
                </summary>
                <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)", marginBottom: 10 }}>{t("pas_colours_hint")}</div>
                <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
                    {/* Letters column */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{t("pas_letters")}</span>
                        {SHELVES.map(s => {
                            const val = palette.shelf[s] ?? DEFAULT_SHELF_COLOURS[s];
                            return (
                                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                                    <span style={{ width: 16, fontWeight: 600 }}>{s}</span>
                                    <input type="color" value={safeSwatch(val)}
                                        onChange={e => setPalette(p => ({ ...p, shelf: { ...p.shelf, [s]: e.target.value } }))} />
                                    <input type="text" value={val} placeholder="#RRGGBB"
                                        style={{ width: 96, fontFamily: "monospace", fontSize: 12 }}
                                        onChange={e => setPalette(p => ({ ...p, shelf: { ...p.shelf, [s]: normaliseHex(e.target.value) } }))} />
                                </div>
                            );
                        })}
                    </div>
                    {/* Numbers column */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{t("pas_numbers")}</span>
                        {POSITIONS.map(p => {
                            const val = palette.position[String(p)] ?? DEFAULT_POSITION_COLOURS[String(p)];
                            return (
                                <div key={p} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                                    <span style={{ width: 16, fontWeight: 600 }}>{p}</span>
                                    <input type="color" value={safeSwatch(val)}
                                        onChange={e => setPalette(prev => ({ ...prev, position: { ...prev.position, [String(p)]: e.target.value } }))} />
                                    <input type="text" value={val} placeholder="#RRGGBB"
                                        style={{ width: 96, fontFamily: "monospace", fontSize: 12 }}
                                        onChange={e => setPalette(prev => ({ ...prev, position: { ...prev.position, [String(p)]: normaliseHex(e.target.value) } }))} />
                                </div>
                            );
                        })}
                    </div>
                    {/* Light colours — colour-wheel only (active vs order-complete) */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{t("pas_light_colours")}</span>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <span style={{ width: 64 }}>{t("pas_light_active")}</span>
                            <select className="admin-settings-select" value={palette.light.active}
                                onChange={e => setPalette(p => ({ ...p, light: { ...p.light, active: e.target.value } }))}>
                                {WHEEL_COLOUR_NAMES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <span style={{ width: 64 }}>{t("pas_light_complete")}</span>
                            <select className="admin-settings-select" value={palette.light.complete}
                                onChange={e => setPalette(p => ({ ...p, light: { ...p.light, complete: e.target.value } }))}>
                                {WHEEL_COLOUR_NAMES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }} title={t("pas_light_timeout_hint")}>
                            <span style={{ width: 64 }}>{t("pas_light_timeout")}</span>
                            <select className="admin-settings-select" value={palette.light.timeout}
                                onChange={e => setPalette(p => ({ ...p, light: { ...p.light, timeout: e.target.value } }))}>
                                {WHEEL_COLOUR_NAMES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                    </div>
                </div>
            </details>

            {/* Light agents — add agents and assign which warehouse each drives. */}
            <details className="lighting-section">
                <summary className="lighting-head">
                    <ChevronRight size={16} className="lighting-caret" />
                    <strong>{t("pas_light_agents")}</strong>
                    {/* Collapsed, the one thing that matters is whether the sites are up. */}
                    <span className={`lighting-badge${agents.length > 0 && agents.every(a => a.online) ? " lighting-badge--online" : ""}`}>
                        {agents.filter(a => a.online).length}/{agents.length} {t("pas_online")}
                    </span>
                </summary>
                <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)", margin: "4px 0 10px" }}>{t("pas_light_agents_hint")}</div>
                {agents.length === 0 && (
                    <div style={{ fontSize: 13, color: "var(--color-text-muted, #6b7280)", marginBottom: 10 }}>{t("pas_no_agents")}</div>
                )}
                {/* One card per device: identity + live status on top, what it
                    reports about itself in the middle, and the four assignment
                    controls in a labelled row — so the selects read as a form,
                    not a soup of anonymous dropdowns. */}
                <div className="lighting-agent-cards">
                    {agents.map(a => {
                        const backendLabel = backendLabelOf(a.net_info?.backend);
                        return (
                            <div key={a.agent_id} style={{ border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
                                {/* Identity + status + actions */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span title={a.online ? t("pas_online") : t("pas_offline")} style={{ width: 9, height: 9, borderRadius: "50%", background: a.online ? "#16a34a" : "#9ca3af", display: "inline-block", flex: "none" }} />
                                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{a.agent_id}</span>
                                    <span style={{ color: "var(--color-text-muted, #6b7280)", fontSize: 12 }}>
                                        {a.online ? t("pas_online") : t("pas_offline")}
                                        {a.cue_latency_ms != null && <span title={t("pas_cue_latency_hint")} style={{ fontFamily: "monospace" }}> · ~{a.cue_latency_ms}ms</span>}
                                    </span>
                                    <button type="button" title={t("pas_wifi")} aria-label={t("pas_wifi")}
                                        onClick={() => openWifi(a.agent_id)}
                                        style={{ marginLeft: "auto", color: a.net_info?.ssid ? "var(--color-primary, #FF7315)" : "var(--color-text-muted, #6b7280)", background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex" }}>
                                        <Wifi size={16} />
                                    </button>
                                    <button type="button" title={t("pas_remove_agent")} aria-label={t("pas_remove_agent")}
                                        onClick={() => removeAgent(a.agent_id)}
                                        style={{ color: "#dc2626", background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex" }}>
                                        <X size={16} />
                                    </button>
                                </div>
                                {/* What the device reports about itself */}
                                <div style={{ color: "var(--color-text-muted, #6b7280)", fontSize: 12, margin: "4px 0 8px", display: "flex", flexWrap: "wrap", gap: 12 }}>
                                    <span>
                                        {a.net_info?.ssid
                                            ? <>{a.net_info.ssid}{a.net_info.signal != null && ` · ${a.net_info.signal}%`}{a.net_info.ips?.wlan0 && <span style={{ fontFamily: "monospace" }}> · {a.net_info.ips.wlan0}</span>}</>
                                            : t("pas_wifi_not_reported")}
                                    </span>
                                    {a.pending_command && (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                            <Loader2 size={11} style={{ animation: "fl-spin 1s linear infinite" }} /> {t("pas_wifi_waiting")}
                                        </span>
                                    )}
                                </div>
                                {/* Assignment controls — each labelled, so the card reads as a form */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                    {([
                                        {
                                            label: t("pas_agent_warehouse"),
                                            el: (
                                                <select className="admin-settings-select" value={a.warehouse ?? ""} onChange={e => assignAgent(a.agent_id, e.target.value, a.station ?? "")}>
                                                    <option value="">{t("pas_unassigned")}</option>
                                                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                                                </select>
                                            ),
                                        },
                                        {
                                            label: t("pas_agent_station"),
                                            title: t("pas_light_agent_station_hint"),
                                            el: (
                                                <select className="admin-settings-select" value={a.station ?? ""} disabled={!a.warehouse}
                                                    onChange={e => assignAgent(a.agent_id, a.warehouse ?? "", e.target.value)}>
                                                    <option value="">{t("pas_station_all")}</option>
                                                    {STATIONS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                                                </select>
                                            ),
                                        },
                                        {
                                            // Pin the device to ONE bay (the Sorter 2 put wall is a
                                            // separate structure with its own node); a pinned bay drops
                                            // out of the station-wide agents.
                                            label: t("pas_agent_bay"),
                                            title: t("pas_light_agent_bay_hint"),
                                            el: (
                                                <select className="admin-settings-select" value={a.bay_id == null ? "" : String(a.bay_id)} disabled={!a.warehouse || !a.station}
                                                    onChange={e => assignAgent(a.agent_id, a.warehouse ?? "", a.station ?? "", e.target.value)}>
                                                    <option value="">{t("pas_bay_all")}</option>
                                                    {agentBays
                                                        .filter(b => b.warehouse === a.warehouse && b.station === a.station)
                                                        .map(b => <option key={String(b.bay_id)} value={String(b.bay_id)}>{b.name}</option>)}
                                                </select>
                                            ),
                                        },
                                        {
                                            // Shows the device's own report, not the queued intent — a
                                            // pending switch reads as "old value + spinner" until it flips.
                                            label: t("pas_agent_backend"),
                                            title: t("pas_backend_hint"),
                                            highlight: backendLabel === "production",
                                            el: (
                                                <select className="admin-settings-select" value={backendLabel ?? ""} disabled={!a.net_info?.backend || !!a.pending_command}
                                                    onChange={e => switchBackend(a.agent_id, e.target.value)}>
                                                    {!a.net_info?.backend && <option value="">{t("pas_backend_unknown")}</option>}
                                                    {backendLabel === "custom" && <option value="custom">{a.net_info?.backend}</option>}
                                                    <option value="staging">{t("pas_backend_staging")}</option>
                                                    <option value="production">{t("pas_backend_production")}</option>
                                                </select>
                                            ),
                                        },
                                        {
                                            // One-shot pushes to the fixtures; the select resets to
                                            // its placeholder so it reads as an action, not a state.
                                            label: t("pas_agent_heads"),
                                            title: t("pas_heads_hint"),
                                            el: (
                                                <select className="admin-settings-select" value="" disabled={!a.online || !!a.pending_command}
                                                    onChange={e => pushHeadPersonality(a.agent_id, e.target.value)}>
                                                    <option value="">{t("pas_heads_choose")}</option>
                                                    <option value="speed_2">{t("pas_heads_speed_2")}</option>
                                                    <option value="speed_1">{t("pas_heads_speed_1")}</option>
                                                    <option value="wifly_off">{t("pas_heads_wifly_off")}</option>
                                                    <option value="pan_tilt_reset">{t("pas_heads_pan_tilt_reset")}</option>
                                                </select>
                                            ),
                                        },
                                    ] as { label: string; title?: string; highlight?: boolean; el: JSX.Element }[]).map(c => (
                                        <label key={c.label} title={c.title} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 110, flex: 1 }}>
                                            <span style={{ fontSize: 11, color: c.highlight ? "var(--color-primary, #FF7315)" : "var(--color-text-muted, #6b7280)", fontWeight: c.highlight ? 600 : 400 }}>{c.label}</span>
                                            {c.el}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Pre-register an agent by the AGENT_ID it reports (e.g. portal-pd),
                    then assign its warehouse from the row above. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--color-border, #e5e7eb)" }}>
                    <input className="admin-settings-input" type="text" value={newAgentId} placeholder={t("pas_agent_id_placeholder")}
                        style={{ fontFamily: "monospace", fontSize: 13, minWidth: 200 }}
                        onChange={e => setNewAgentId(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") addAgent(); }} />
                    <button type="button" className="btn btn-success btn-sm" disabled={!newAgentId.trim()} onClick={addAgent}>
                        <Plus size={14} /> {t("pas_add_agent")}
                    </button>
                </div>
            </details>

            {/* Per-warehouse light settings: brightness + auto-off delay. */}
            <details className="lighting-section">
                <summary className="lighting-head">
                    <ChevronRight size={16} className="lighting-caret" />
                    <strong>{t("pas_light_settings")}</strong>
                    <span className="lighting-badge">{warehouse}</span>
                    <span className="lighting-head-status">
                        <SaveStatus dirty={lightSettingsDirty} saving={savingLightSettings} />
                    </span>
                </summary>
                <div className="filter-pills" role="tablist" style={{ margin: "10px 0" }}>
                    {warehouses.map(w => (
                        <button key={w} type="button" role="tab" aria-selected={warehouse === w}
                            className={`filter-pill${warehouse === w ? " filter-pill--active" : ""}`}
                            onClick={() => setWarehouse(w)}>
                            {w}
                        </button>
                    ))}
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        {t("pas_brightness")}
                        <input className="admin-settings-input" type="number" min={0} max={100} style={{ width: 70 }}
                            value={lightSettings.brightness}
                            onChange={e => setLightSettings(s => ({ ...s, brightness: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))} />
                        <span style={{ color: "var(--color-text-muted, #6b7280)" }}>%</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        {t("pas_light_off_delay")}
                        <input className="admin-settings-input" type="number" min={0} max={600} style={{ width: 70 }}
                            value={Math.round(lightSettings.off_ms / 1000)}
                            onChange={e => setLightSettings(s => ({ ...s, off_ms: Math.max(0, Math.min(600, Number(e.target.value) || 0)) * 1000 }))} />
                        <span style={{ color: "var(--color-text-muted, #6b7280)" }}>s</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        {t("pas_move_base")}
                        <input className="admin-settings-input" type="number" min={0} max={5000} step={10} style={{ width: 80 }}
                            value={lightSettings.move_base_ms}
                            onChange={e => setLightSettings(s => ({ ...s, move_base_ms: Math.max(0, Math.min(5000, Number(e.target.value) || 0)) }))} />
                        <span style={{ color: "var(--color-text-muted, #6b7280)" }}>ms</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        {t("pas_move_step")}
                        <input className="admin-settings-input" type="number" min={0} max={2000} step={10} style={{ width: 80 }}
                            value={lightSettings.move_step_ms}
                            onChange={e => setLightSettings(s => ({ ...s, move_step_ms: Math.max(0, Math.min(2000, Number(e.target.value) || 0)) }))} />
                        <span style={{ color: "var(--color-text-muted, #6b7280)" }}>ms</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        {t("pas_move_threshold")}
                        <input className="admin-settings-input" type="number" min={0} max={65535} step={250} style={{ width: 90 }}
                            value={lightSettings.move_threshold}
                            onChange={e => setLightSettings(s => ({ ...s, move_threshold: Math.max(0, Math.min(65535, Number(e.target.value) || 0)) }))} />
                    </label>
                </div>
                {/* The beam stays dark while the head slews and lights when this
                    window expires — lower = faster response, too low and the
                    beam is visibly still moving when it lights. */}
                <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)", marginTop: 8 }}>
                    {t("pas_move_timing_hint")}
                </div>
            </details>

            {/* Per-agent network / WiFi modal. */}
            {wifiAgent && (
                <div className="modal-overlay" onClick={() => setWifiAgentId(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <div className="modal-card-header">
                            <strong>{t("pas_wifi_title", { id: wifiAgent.agent_id })}</strong>
                            <button className="modal-close" onClick={() => setWifiAgentId(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body" style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 12 }}>
                            {/* Current connection, as last self-reported by the device. */}
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("pas_wifi_current")}</div>
                                {wifiAgent.net_info ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <span>
                                            <Wifi size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                                            {wifiAgent.net_info.ssid
                                                ? <>{wifiAgent.net_info.ssid}{wifiAgent.net_info.signal != null && <span style={{ color: "var(--color-text-muted, #6b7280)" }}> — {wifiAgent.net_info.signal}%</span>}</>
                                                : <span style={{ color: "var(--color-text-muted, #6b7280)" }}>{wifiAgent.net_info.error ?? "—"}</span>}
                                        </span>
                                        {Object.entries(wifiAgent.net_info.ips ?? {}).map(([iface, ip]) => (
                                            <span key={iface} style={{ fontFamily: "monospace", fontSize: 12, color: "var(--color-text-muted, #6b7280)" }}>{iface}: {ip}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ color: "var(--color-text-muted, #6b7280)" }}>{t("pas_wifi_not_reported")}</div>
                                )}
                            </div>

                            {/* Command in flight — the device collects it on its ~15s poll. */}
                            {wifiAgent.pending_command ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-muted, #6b7280)" }}>
                                    <Loader2 size={14} style={{ animation: "fl-spin 1s linear infinite" }} />
                                    {t("pas_wifi_waiting")}
                                    {!wifiAgent.online && <span style={{ color: "#d97706" }}>{t("pas_wifi_offline_warn")}</span>}
                                </div>
                            ) : (
                                <div>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => queueWifi({ action: "scan" })}>
                                        <Wifi size={14} /> {t("pas_wifi_scan")}
                                    </button>
                                </div>
                            )}

                            {/* Last outcome: a connect result banner, or the scan's network list. */}
                            {wifiAgent.command_result?.type === "wifi_connect" && (
                                <div style={{ color: wifiAgent.command_result.ok ? "#16a34a" : "#dc2626" }}>
                                    {t(wifiAgent.command_result.ok ? "pas_wifi_saved_ok" : "pas_wifi_failed", { detail: wifiAgent.command_result.detail ?? "" })}
                                </div>
                            )}
                            {wifiAgent.command_result?.type === "wifi_scan" && !wifiAgent.command_result.ok && (
                                <div style={{ color: "#dc2626" }}>{t("pas_wifi_scan_failed", { detail: wifiAgent.command_result.detail ?? "" })}</div>
                            )}
                            {wifiAgent.command_result?.type === "wifi_scan" && wifiAgent.command_result.ok && (
                                <div>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("pas_wifi_networks")}</div>
                                    {(wifiAgent.command_result.networks ?? []).length === 0 && (
                                        <div style={{ color: "var(--color-text-muted, #6b7280)" }}>{t("pas_wifi_no_networks")}</div>
                                    )}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 180, overflowY: "auto" }}>
                                        {(wifiAgent.command_result.networks ?? []).map(n => (
                                            <button key={n.ssid} type="button"
                                                onClick={() => { setWifiSsid(n.ssid); setWifiPsk(""); }}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", fontSize: 13,
                                                    background: wifiSsid === n.ssid ? "var(--color-surface-alt, #f3f4f6)" : "none",
                                                    border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 6, cursor: "pointer", textAlign: "left",
                                                }}>
                                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.ssid}</span>
                                                {n.in_use && <span style={{ color: "#16a34a", fontSize: 11 }}>{t("pas_wifi_in_use")}</span>}
                                                {!n.security && <span style={{ color: "var(--color-text-muted, #6b7280)", fontSize: 11 }}>{t("pas_wifi_open")}</span>}
                                                <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--color-text-muted, #6b7280)" }}>{n.signal}%</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Join / save a network — picked from the scan or typed by hand. */}
                            <div style={{ borderTop: "1px solid var(--color-border, #e5e7eb)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)" }}>{t("pas_wifi_manual")}</div>
                                <input className="admin-settings-input" type="text" value={wifiSsid} placeholder={t("pas_wifi_ssid")} maxLength={32}
                                    onChange={e => setWifiSsid(e.target.value)} />
                                <input className="admin-settings-input" type="password" value={wifiPsk} placeholder={t("pas_wifi_password")} maxLength={63}
                                    onChange={e => setWifiPsk(e.target.value)} />
                                <div style={{ fontSize: 11, color: "var(--color-text-muted, #6b7280)" }}>{t("pas_wifi_save_hint")}</div>
                                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 4 }}>
                                    <button type="button" className="btn btn-primary btn-sm"
                                        disabled={!wifiSsid.trim() || !!wifiAgent.pending_command}
                                        onClick={() => wifiConnect(false)}>
                                        {t("pas_wifi_save")}
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-sm"
                                        disabled={!wifiSsid.trim() || !!wifiAgent.pending_command}
                                        onClick={() => wifiConnect(true)}>
                                        {t("pas_wifi_save_switch")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SortingSharedLighting;
